// static/table_engine_src/core/ops_rows.js

function isLockedCol(col) {
  return !!col?.locked;
}

/**
 * Installs on ctx:
 * - ctx.clearCell / clearRange
 * - ctx.clearSelectedColumns / clearOutputsOnly
 * - ctx.clearSelectedRowsInputs / clearSelectedRowsOutputs
 * - ctx.insertRowAbove / insertRowBelow / duplicateRows / deleteRows
 */
export function installRowOps(ctx, { spec, onEdit, onAction } = {}) {
  if (!ctx || typeof ctx !== "object") return;

  const { cols, colByKey, hiddenCols } = ctx;
  if (!Array.isArray(cols) || !colByKey || !hiddenCols) return;

  function cloneForHistory(value) {
    if (value == null) return value;

    if (typeof structuredClone === "function") {
      try {
        return structuredClone(value);
      } catch {}
    }

    try {
      return JSON.parse(JSON.stringify(value));
    } catch {
      return value;
    }
  }

  function pushHistoryAction(action) {
    if (!action || typeof action !== "object") return;
    if (typeof ctx.pushHistoryAction !== "function") return;
    ctx.pushHistoryAction({
      ...action,
      ts: Number.isFinite(action.ts) ? action.ts : Date.now(),
    });
  }

  function normalizeInsertAt(insertAt, nRows) {
    const n = Math.max(0, Number.isInteger(nRows) ? nRows : 0);
    if (!Number.isInteger(insertAt) || insertAt < 0 || insertAt > n) return n;
    return insertAt;
  }

  function snapshotRowItem(tr) {
    if (!tr) return {};

    if (typeof ctx.snapshotRowItem === "function") {
      const snap = ctx.snapshotRowItem(tr);
      return cloneForHistory(snap || {});
    }

    const fallback = ctx.readRow?.(tr, { includeOutputs: true }) || {};
    return cloneForHistory(fallback);
  }

  function removeRowsByIndexInternal(rowIdxs = []) {
    if (!Array.isArray(rowIdxs) || !rowIdxs.length) return 0;

    if (typeof ctx.removeRowsByIndex === "function") {
      return Number(ctx.removeRowsByIndex(rowIdxs)) || 0;
    }

    const rows = ctx.getRowEls?.() || [];
    const removedRows = [];

    rowIdxs.forEach((r) => {
      if (!Number.isInteger(r) || r < 0 || r >= rows.length) return;
      const tr = rows[r];
      if (!tr) return;
      removedRows.push(tr);
      tr.remove();
    });

    ctx.updateRowHandles?.();

    if (removedRows.length) {
      if (typeof ctx.markTotalsRowsRemoved === "function") {
        ctx.markTotalsRowsRemoved(removedRows);
      } else {
        ctx.markTotalsDirtyAll?.();
      }
    }

    return removedRows.length;
  }

  function restoreRowsAt(entries = []) {
    const sorted = [...entries]
      .filter((e) => e && Number.isInteger(e.index) && e.index >= 0)
      .sort((a, b) => a.index - b.index);

    let count = 0;
    sorted.forEach((entry) => {
      const tr = ctx.renderRow?.(cloneForHistory(entry.item || {}), { insertAt: entry.index });
      if (tr) count += 1;
    });
    return count;
  }

  function pushRowInsertHistory(entries, label = "insert-rows") {
    const clean = [...(entries || [])]
      .filter((e) => e && Number.isInteger(e.index) && e.index >= 0)
      .map((e) => ({ index: e.index, item: cloneForHistory(e.item || {}) }));
    if (!clean.length) return;

    const removeIdxsDesc = clean.map((e) => e.index).sort((a, b) => b - a);

    pushHistoryAction({
      type: "rows-insert",
      label,
      undo: () => {
        removeRowsByIndexInternal(removeIdxsDesc);
        ctx.updateTotals?.();
      },
      redo: () => {
        restoreRowsAt(clean);
        ctx.updateTotals?.();
      },
    });
  }

  function pushRowDeleteHistory(entries, label = "delete-rows") {
    const clean = [...(entries || [])]
      .filter((e) => e && Number.isInteger(e.index) && e.index >= 0)
      .map((e) => ({ index: e.index, item: cloneForHistory(e.item || {}) }));
    if (!clean.length) return;

    const removeIdxsDesc = clean.map((e) => e.index).sort((a, b) => b - a);

    pushHistoryAction({
      type: "rows-delete",
      label,
      undo: () => {
        restoreRowsAt(clean);
        ctx.updateTotals?.();
      },
      redo: () => {
        removeRowsByIndexInternal(removeIdxsDesc);
        ctx.updateTotals?.();
      },
    });
  }

  function clearCell(r, c) {
    const tr = (ctx.getRowEls?.() || [])[r];
    if (!tr) return;

    const col = cols[c];
    if (!col) return;
    if (hiddenCols.has(col.key)) return;

    if (col.kind === "rowHandle") return;
    if (isLockedCol(col)) return;

    if (col.kind === "input") {
      if (col.readonly) return;
      const el = ctx.getInput?.(tr, col.key);
      if (el) {
        el.value = "";
        ctx.clearRawDatasets?.(el);
      }
    } else if (col.kind === "output") {
      const el = ctx.getOut?.(tr, col.key);
      if (el) {
        el.textContent = "N/A";
        ctx.clearRawDatasets?.(el);
      }
    }
  }

  function clearRange(range) {
    if (!range) return;
    const { r0, r1, c0, c1 } = range;
    const dirty = [];
    for (let r = r0; r <= r1; r++) {
      dirty.push(r);
      for (let c = c0; c <= c1; c++) clearCell(r, c);
    }
    ctx.markTotalsDirtyRows?.(dirty);
    onEdit?.();
    ctx.updateTotals?.();
  }

  function clearSelectedColumns() {
    if (!ctx._sel?.selectedCols?.size) return;

    let cleared = 0;

    ctx._sel.selectedCols.forEach((k) => {
      const col = colByKey.get(k);
      if (!col) return;
      if (col.kind === "rowHandle") return;
      if (isLockedCol(col)) return;
      if (col.kind === "input" && col.readonly) return;

      (ctx.getRowEls?.() || []).forEach((tr) => {
        if (col.kind === "input") {
          const el = ctx.getInput?.(tr, k);
          if (el) {
            el.value = "";
            ctx.clearRawDatasets?.(el);
          }
        } else if (col.kind === "output") {
          const el = ctx.getOut?.(tr, k);
          if (el) {
            el.textContent = "N/A";
            ctx.clearRawDatasets?.(el);
          }
        }
      });

      cleared++;
    });

    if (!cleared) {
      ctx.notify?.err?.("Nothing cleared (selection is protected/readonly).");
      return;
    }

    ctx.markTotalsDirtyAll?.();
    ctx.notify?.ok?.(`Cleared ${cleared} column(s).`);
    onEdit?.();
    ctx.updateTotals?.();
  }

  function clearOutputsOnly() {
    cols.forEach((c) => {
      if (c.kind !== "output") return;
      if (isLockedCol(c)) return;

      (ctx.getRowEls?.() || []).forEach((tr) => {
        const el = ctx.getOut?.(tr, c.key);
        if (el) {
          el.textContent = "N/A";
          ctx.clearRawDatasets?.(el);
        }
      });
    });

    ctx.markTotalsDirtyAll?.();
    ctx.notify?.ok?.("Cleared outputs.");
    onAction?.();
    ctx.updateTotals?.();
  }

  function clearRowInputs(rIdx) {
    const tr = (ctx.getRowEls?.() || [])[rIdx];
    if (!tr) return;

    cols.forEach((c) => {
      if (c.kind !== "input") return;
      if (c.readonly) return;
      if (isLockedCol(c)) return;
      if (hiddenCols.has(c.key)) return;

      const el = ctx.getInput?.(tr, c.key);
      if (el) {
        el.value = "";
        ctx.clearRawDatasets?.(el);
      }
    });
  }

  function clearRowOutputs(rIdx) {
    const tr = (ctx.getRowEls?.() || [])[rIdx];
    if (!tr) return;

    cols.forEach((c) => {
      if (c.kind !== "output") return;
      if (isLockedCol(c)) return;
      if (hiddenCols.has(c.key)) return;

      const el = ctx.getOut?.(tr, c.key);
      if (el) {
        el.textContent = "N/A";
        ctx.clearRawDatasets?.(el);
      }
    });
  }

  function clearSelectedRowsInputs() {
    if (!ctx._sel?.selectedRows?.size) return;

    const MIN_ROWS = Number.isFinite(spec?.minRows) ? spec.minRows : 0;

    const rowsBefore = ctx.getRowEls?.() || [];
    const nBefore = rowsBefore.length;

    const idxs = [...new Set([...ctx._sel.selectedRows])]
      .filter((i) => Number.isInteger(i))
      .filter((i) => i >= 0 && i < nBefore);

    if (!idxs.length) return;

    idxs.forEach((r) => clearRowInputs(r));
    ctx.markTotalsDirtyRows?.(idxs);

    if (MIN_ROWS > 0) {
      let n = (ctx.getRowEls?.() || []).length;

      if (n < MIN_ROWS) {
        for (let k = n; k < MIN_ROWS; k++) ctx.renderRow?.({}, { insertAt: null });
        ctx.updateRowHandles?.();
        n = (ctx.getRowEls?.() || []).length;
      }

      const clearedAll = idxs.length === nBefore;
      ctx.notify?.ok?.(
        clearedAll
          ? `Cleared inputs for all rows (kept ${MIN_ROWS} blank row(s)).`
          : `Cleared input cells for ${idxs.length} row(s).`,
      );
    } else {
      ctx.notify?.ok?.(`Cleared input cells for ${idxs.length} row(s).`);
    }

    onEdit?.();
    ctx.updateTotals?.();
  }

  function clearSelectedRowsOutputs() {
    if (!ctx._sel?.selectedRows?.size) return;
    const dirty = [...ctx._sel.selectedRows];
    dirty.forEach((r) => clearRowOutputs(r));
    ctx.markTotalsDirtyRows?.(dirty);
    ctx.notify?.ok?.(`Cleared outputs for ${ctx._sel.selectedRows.size} row(s).`);
    onAction?.();
    ctx.updateTotals?.();
  }

  function insertRowAbove(rIdx) {
    const nRows = (ctx.getRowEls?.() || []).length;
    const at = normalizeInsertAt(Math.max(0, Number.isInteger(rIdx) ? rIdx : 0), nRows);
    const tr = ctx.renderRow?.({}, { insertAt: at });
    if (tr) {
      pushRowInsertHistory([{ index: at, item: snapshotRowItem(tr) }], "insert-row-above");
    }

    ctx.notify?.ok?.("Inserted row above.");
    onAction?.();
    ctx.updateTotals?.();
  }

  function insertRowBelow(rIdx) {
    const nRows = (ctx.getRowEls?.() || []).length;
    const at = normalizeInsertAt(Math.max(0, (Number.isInteger(rIdx) ? rIdx : 0) + 1), nRows);
    const tr = ctx.renderRow?.({}, { insertAt: at });
    if (tr) {
      pushRowInsertHistory([{ index: at, item: snapshotRowItem(tr) }], "insert-row-below");
    }

    ctx.notify?.ok?.("Inserted row below.");
    onAction?.();
    ctx.updateTotals?.();
  }

  function duplicateRows(rowIdxs) {
    const rows = ctx.getRowEls?.() || [];
    const sorted = [...(rowIdxs || [])].sort((a, b) => a - b);
    if (!sorted.length) return;

    const insertAt = sorted[sorted.length - 1] + 1;

    const snapshots = sorted.map((r) => {
      const tr = rows[r];
      const obj = {};

      cols.forEach((c) => {
        if (c.kind !== "input") return;
        if (c.readonly) return;
        if (isLockedCol(c)) return;

        const el = ctx.getInput?.(tr, c.key);
        if (!el) {
          obj[c.key] = "";
          return;
        }

        if (c.type === "number") {
          const raw = ctx.getAnyRawDataset?.(el);
          obj[c.key] = raw !== "" ? raw : el.value;
        } else {
          obj[c.key] = el.value;
        }
      });

      return obj;
    });

    let at = insertAt;
    const insertedIdxs = [];
    const insertedEntries = [];
    snapshots.forEach((snap) => {
      const insertIdx = at;
      const trNew = ctx.renderRow?.({}, { insertAt: at++ });
      if (!trNew) return;
      insertedIdxs.push(insertIdx);

      Object.entries(snap).forEach(([k, v]) => {
        const col = colByKey.get(k);
        if (!col || col.kind !== "input") return;

        const el = ctx.getInput?.(trNew, k);
        if (!el || el.readOnly) return;

        if (col.type === "number") ctx.setNumericInputValue?.(el, col, v);
        else el.value = String(v ?? "");
      });

      cols.forEach((c) => {
        if (c.kind === "output") {
          const outEl = ctx.getOut?.(trNew, c.key);
          if (outEl) {
            outEl.textContent = "N/A";
            ctx.clearRawDatasets?.(outEl);
          }
        }
      });

      insertedEntries.push({ index: insertIdx, item: snapshotRowItem(trNew) });
    });

    if (insertedEntries.length) pushRowInsertHistory(insertedEntries, "duplicate-rows");
    if (insertedIdxs.length) ctx.markTotalsDirtyRows?.(insertedIdxs);
    ctx.notify?.ok?.(`Duplicated ${sorted.length} row(s).`);
    onEdit?.();
    ctx.updateTotals?.();
  }

  function deleteRows(rowIdxs) {
    const MIN_ROWS = Number.isFinite(spec?.minRows) ? spec.minRows : 0;

    const rows = ctx.getRowEls?.() || [];
    const nBefore = rows.length;

    const idxs = [...new Set((rowIdxs || [])
      .filter((x) => Number.isInteger(x))
      .filter((i) => i >= 0 && i < nBefore)
    )].sort((a, b) => b - a);

    if (!idxs.length) return;

    const mustKeep = Math.max(0, MIN_ROWS);
    const maxDeletable = Math.max(0, nBefore - mustKeep);

    if (maxDeletable <= 0) {
      ctx.notify?.info?.(`Must keep at least ${mustKeep} row(s).`);
      return;
    }

    const toDelete = idxs.slice(0, maxDeletable);

    if (toDelete.length < idxs.length) {
      ctx.notify?.info?.(`Keeping at least ${mustKeep} row(s).`);
    }

    const toDeleteAsc = [...toDelete].sort((a, b) => a - b);
    const removedEntries = toDeleteAsc.map((idx) => {
      const tr = rows[idx];
      return { index: idx, item: snapshotRowItem(tr) };
    });

    removeRowsByIndexInternal(toDelete);
    if (removedEntries.length) pushRowDeleteHistory(removedEntries, "delete-rows");

    ctx.notify?.ok?.(`Deleted ${toDelete.length} row(s).`);
    onAction?.();
    ctx.updateTotals?.();
    ctx.scheduleNotifyRendered?.();
  }

  // expose
  ctx.clearCell = clearCell;
  ctx.clearRange = clearRange;

  ctx.clearSelectedColumns = clearSelectedColumns;
  ctx.clearOutputsOnly = clearOutputsOnly;

  ctx.clearSelectedRowsInputs = clearSelectedRowsInputs;
  ctx.clearSelectedRowsOutputs = clearSelectedRowsOutputs;

  ctx.insertRowAbove = insertRowAbove;
  ctx.insertRowBelow = insertRowBelow;
  ctx.duplicateRows = duplicateRows;
  ctx.deleteRows = deleteRows;
}
